import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import { getOnsiteHeader } from "./utils";

const EVENTS = [
  {
    key: "current",
    name: "PMTX 2026",
    year: 2026,
    pb: "pmtx2026-imtma",
    color: "#2563eb",
    dates: ["2026-09-07", "2026-09-08", "2026-09-09", "2026-09-10"],
  },
  {
    key: "previous",
    name: "PMTX 2024",
    year: 2024,
    pb: "pmtx2024-imtma",
    color: "#f59e0b",
    dates: ["2024-05-25", "2024-05-26", "2024-05-27", "2024-05-28"],
  },
];

const DAYS = Array.from({ length: 5 }, (_, i) => `day${i + 1}`);
const DAY_MS = 24 * 60 * 60 * 1000;
const IST_OFFSET_MS = 330 * 60 * 1000;

function getDefaultThroughDay(now = new Date()) {
  const todayIST = new Date(now.getTime() + IST_OFFSET_MS)
    .toISOString()
    .slice(0, 10);

  const currentEvent = EVENTS[0];

  const day =
    Math.floor(
      (Date.parse(todayIST) - Date.parse(currentEvent.dates[0])) / DAY_MS
    ) + 1;

  // Before the event: Day 1.
  // During the event: through the current day.
  // After the event: through the final scheduled day.
  return Math.max(1, Math.min(currentEvent.dates.length, DAYS.length, day));
}

function comparisonLabel(day) {
  return day === 1 ? "Day 1 Comparison" : `Day 1–${day} Comparison`;
}

function eventDateLabel(event, index) {
  const date = event.dates[index];

  if (!date) {
    return `Day ${index + 1} (date not set)`;
  }

  const [year, month, day] = date.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${day}-${months[Number(month) - 1]}-${year.slice(-2)}`;
}

function registrationColumn(role) {
  if (["Special Invitee", "Delegation", "EC", "Media"].includes(role)) {
    return "special";
  }

  if (role === "Students") return "students";
  if (role === "Visitor") return "preRegistration";
  if (role === "Onsite Visitor") return "spotRegistration";

  return "other";
}

const TABLE_FIELDS = [
  "special",
  "students",
  "preRegistration",
  "spotRegistration",
  "other",
  "repeats",
  "visits",
];

const shareText = (value, total) =>
  total ? `${((value / total) * 100).toFixed(1)}%` : "0%";

const signedNumber = (value) => `${value > 0 ? "+" : ""}${number(value)}`;

const differencePercent = (current, previous) =>
  previous
    ? `${current > previous ? "+" : ""}${(
        ((current - previous) / previous) *
        100
      ).toFixed(2)}%`
    : current
    ? "N/A"
    : "0.00%";

const ROLE_ORDER = [
  "Special Invitee",
  "Delegation",
  "EC",
  "Media",
  "Students",
  "Onsite Visitor",
  "Visitor",
  "Other",
];

const number = (value) => Number(value || 0).toLocaleString("en-IN");

const isScanned = (value) => value === 1 || value === true || value === "1";

function getRole(role) {
  const raw = String(role || "").trim();

  // Preserve the existing visitor / VISITOR distinction.
  if (raw === "visitor") return "Visitor";

  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");

  if (["EXHIBITOR", "SERVICE"].includes(key)) {
    return null;
  }

  if (["VIP", "SPECIAL_INVITEE"].includes(key)) {
    return "Special Invitee";
  }

  if (["DELEGATION", "TRADE_DELEGATE"].includes(key)) {
    return "Delegation";
  }

  if (["STUDENT", "STUDENTS", "BULK_STUDENT"].includes(key)) {
    return "Students";
  }

  if (key === "VISITOR") return "Onsite Visitor";
  if (key === "MEDIA") return "Media";
  if (key === "EC") return "EC";

  return "Other";
}

function validTimestamp(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

// Day flags decide attendance.
// Timestamps are only needed for hourly charts.
function aggregateEvent(records, throughDay) {
  const people = new Map();

  records.forEach((record, index) => {
    const role = getRole(record.role);
    if (!role) return;

    const id =
      record._id != null
        ? `id:${String(record._id)}`
        : record.user_id != null && record.user_id !== ""
        ? `user:${String(record.user_id)}`
        : `row:${index}`;

    if (!people.has(id)) {
      people.set(id, {
        role,
        scans: new Map(),
      });
    }

    const person = people.get(id);

    DAYS.slice(0, throughDay).forEach((day, dayIndex) => {
      if (!isScanned(record[day])) return;

      const timestamp = validTimestamp(record[`${day}timestamp`]);
      const existing = person.scans.get(dayIndex);

      if (
        !person.scans.has(dayIndex) ||
        (timestamp !== null && (existing === null || timestamp < existing))
      ) {
        person.scans.set(dayIndex, timestamp);
      }
    });
  });

  const days = DAYS.slice(0, throughDay).map((_, index) => ({
    name: `Day ${index + 1}`,
    visits: 0,
    newVisitors: 0,
    repeats: 0,
    cumulative: 0,
    missingTime: 0,
    hours: Array(24).fill(0),
    special: 0,
    students: 0,
    preRegistration: 0,
    spotRegistration: 0,
    other: 0,
  }));

  const roles = Object.fromEntries(ROLE_ORDER.map((role) => [role, 0]));

  let unique = 0;
  let visits = 0;

  people.forEach((person) => {
    if (!person.scans.size) return;

    unique++;
    roles[person.role]++;

    const firstDay = Math.min(...person.scans.keys());

    person.scans.forEach((timestamp, dayIndex) => {
      const day = days[dayIndex];

      visits++;
      day.visits++;

      if (dayIndex === firstDay) {
        day.newVisitors++;
        day[registrationColumn(person.role)]++;
      } else {
        // Returning attendees only appear in Repeat Visitors.
        day.repeats++;
      }

      if (timestamp === null) {
        day.missingTime++;
      } else {
        const hour = new Date(timestamp + 330 * 60 * 1000).getUTCHours();

        day.hours[hour]++;
      }
    });
  });

  let cumulative = 0;

  days.forEach((day) => {
    cumulative += day.visits;
    day.cumulative = cumulative;
  });

  const totals = Object.fromEntries(
    TABLE_FIELDS.map((field) => [
      field,
      days.reduce((sum, day) => sum + day[field], 0),
    ])
  );

  return {
    days,
    roles,
    totals,
    unique,
    visits,
    repeats: visits - unique,
  };
}

function changeText(current, previous) {
  if (previous === 0) {
    return current === 0 ? "No change" : "N/A · 2024 is zero";
  }

  const percent = ((current - previous) / previous) * 100;

  return `${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`;
}

function ChangeChip({ current, previous }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={changeText(current, previous)}
      color={
        previous === 0 || current === previous
          ? "default"
          : current > previous
          ? "success"
          : "error"
      }
    />
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {subtitle}
        </Typography>

        <Box
          sx={{
            height: 320,
            width: "100%",
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

function MetricCard({ title, note, metric, stats }) {
  const current = stats.current[metric];
  const previous = stats.previous[metric];

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Typography color="text.secondary" fontWeight={600}>
          {title}
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          my={2}
        >
          {EVENTS.map((event) => (
            <Box key={event.key}>
              <Typography
                variant="caption"
                color={event.color}
                fontWeight={700}
              >
                {event.name}
              </Typography>

              <Typography variant="h4" fontWeight={800}>
                {number(stats[event.key][metric])}
              </Typography>
            </Box>
          ))}
        </Stack>

        <ChangeChip current={current} previous={previous} />

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1}
        >
          {note}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ComparisonTable({ stats, throughDay }) {
  const showOther =
    stats.current.totals.other > 0 || stats.previous.totals.other > 0;

  const columns = [
    {
      key: "special",
      label: "Special Invitee / EC / Trade Delegates / Media",
    },
    {
      key: "students",
      label: "Students",
    },
    {
      key: "preRegistration",
      label: "Pre-Registration",
    },
    {
      key: "spotRegistration",
      label: "Spot Registration",
    },
    ...(showOther ? [{ key: "other", label: "Other" }] : []),
    {
      key: "repeats",
      label: "Repeat Visitors",
    },
    {
      key: "visits",
      label: "Visitors",
    },
  ];

  const orderedEvents = [EVENTS[1], EVENTS[0]];

  const deltaStyle = (current, previous) => ({
    color:
      current < previous
        ? "#b91c1c"
        : current > previous
        ? "#166534"
        : "text.primary",
    fontWeight: 700,
  });

  return (
    <Card variant="outlined" sx={{ mt: 3, borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>
          Day-by-day breakdown
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {comparisonLabel(throughDay)} · % is each day's share of that event's
          selected total. Difference = 2026 − 2024; difference % uses 2024 as
          the baseline.
        </Typography>
      </CardContent>

      <TableContainer>
        <Table
          size="small"
          sx={{
            minWidth: 1200,
            borderCollapse: "collapse",
            "& th, & td": {
              border: "1px solid #94a3b8",
              textAlign: "center",
              px: 1.5,
              py: 1.2,
            },
            "& thead th": {
              bgcolor: "#365f8c",
              color: "white",
              fontWeight: 700,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>Year</TableCell>

              <TableCell rowSpan={2} sx={{ minWidth: 130 }}>
                Date
              </TableCell>

              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  rowSpan={2}
                  sx={{
                    minWidth: column.key === "special" ? 170 : 95,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}

              <TableCell rowSpan={2}>%</TableCell>

              <TableCell colSpan={2}>Difference: 2026 − 2024</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Visitors</TableCell>
              <TableCell>%</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {orderedEvents.map((event) => {
              const result = stats[event.key];
              const isCurrent = event.key === "current";

              return (
                <React.Fragment key={event.key}>
                  {result.days.map((day, index) => {
                    const baseline = stats.previous.days[index].visits;

                    return (
                      <TableRow
                        key={`${event.key}-${index}`}
                        sx={{
                          bgcolor: isCurrent ? "#f8fbff" : "white",
                        }}
                      >
                        {index === 0 && (
                          <TableCell
                            component="th"
                            scope="rowgroup"
                            rowSpan={result.days.length}
                            sx={{
                              fontWeight: 700,
                              fontSize: 16,
                            }}
                          >
                            {event.year}
                          </TableCell>
                        )}

                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {eventDateLabel(event, index)}
                        </TableCell>

                        {columns.map((column) => (
                          <TableCell
                            key={column.key}
                            sx={{
                              fontWeight: column.key === "visits" ? 800 : 400,
                            }}
                          >
                            {number(day[column.key])}
                          </TableCell>
                        ))}

                        <TableCell sx={{ fontWeight: 700 }}>
                          {shareText(day.visits, result.visits)}
                        </TableCell>

                        <TableCell
                          sx={isCurrent ? deltaStyle(day.visits, baseline) : {}}
                        >
                          {isCurrent
                            ? signedNumber(day.visits - baseline)
                            : "—"}
                        </TableCell>

                        <TableCell
                          sx={isCurrent ? deltaStyle(day.visits, baseline) : {}}
                        >
                          {isCurrent
                            ? differencePercent(day.visits, baseline)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  <TableRow
                    sx={{
                      bgcolor: "#fff200",
                      "& td, & th": {
                        fontWeight: 800,
                      },
                    }}
                  >
                    <TableCell component="th" scope="row" colSpan={2}>
                      Total PMTX - {event.year}
                    </TableCell>

                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        {number(result.totals[column.key])}
                      </TableCell>
                    ))}

                    <TableCell>{result.visits ? "100%" : "0%"}</TableCell>

                    <TableCell
                      sx={
                        isCurrent
                          ? deltaStyle(result.visits, stats.previous.visits)
                          : {}
                      }
                    >
                      {isCurrent
                        ? signedNumber(result.visits - stats.previous.visits)
                        : "—"}
                    </TableCell>

                    <TableCell
                      sx={
                        isCurrent
                          ? deltaStyle(result.visits, stats.previous.visits)
                          : {}
                      }
                    >
                      {isCurrent
                        ? differencePercent(
                            result.visits,
                            stats.previous.visits
                          )
                        : "—"}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Category columns count first-time attendees. Later attended days count
          only under Repeat Visitors. Pre-Registration uses role "visitor"; Spot
          Registration uses role "VISITOR".
        </Typography>

        {throughDay > EVENTS[0].dates.length && (
          <Typography variant="caption" color="text.secondary" display="block">
            The supplied schedule lists four days. Day 5 attendance is included
            if recorded, but its date is not set.
          </Typography>
        )}
      </Box>
    </Card>
  );
}

export default function FootfallComparison() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  const [throughDay, setThroughDay] = useState(() => getDefaultThroughDay());
  const [hourlyDay, setHourlyDay] = useState(() => getDefaultThroughDay());

  const requestRef = useRef(null);

  const loadData = useCallback(async () => {
    requestRef.current?.abort();

    const controller = new AbortController();
    requestRef.current = controller;

    setLoading(true);
    setErrors([]);
    setData(null);
    setUpdatedAt(null);

    const results = await Promise.allSettled(
      EVENTS.map(async (event) => {
        const config = getOnsiteHeader(ONSITE_MODULE_ITEMS.FOOTFALL_ANALYSIS);

        const response = await axios.get(
          `${API_ENDPOINT}/onsite/scanned-users-footfall-small-comparison`,
          {
            ...config,
            params: {
              ...config?.params,
              pb: event.pb,
            },
            signal: controller.signal,
            timeout: 60000,
          }
        );

        if (!Array.isArray(response.data)) {
          throw new Error("The server did not return a footfall array.");
        }

        return response.data;
      })
    );

    if (controller.signal.aborted) return;

    const failures = results.flatMap((result, index) => {
      if (result.status === "fulfilled") return [];

      const serverMessage = result.reason?.response?.data?.message;

      const message =
        typeof serverMessage === "string"
          ? serverMessage
          : result.reason?.message || "Unable to load data.";

      return [`${EVENTS[index].name}: ${message}`];
    });

    setErrors(failures);

    if (!failures.length) {
      setData({
        current: results[0].value,
        previous: results[1].value,
      });
      setUpdatedAt(new Date());
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();

    return () => requestRef.current?.abort();
  }, [loadData]);

  const stats = useMemo(
    () =>
      data
        ? {
            current: aggregateEvent(data.current, throughDay),
            previous: aggregateEvent(data.previous, throughDay),
          }
        : null,
    [data, throughDay]
  );

  const dailyData = useMemo(
    () =>
      stats
        ? stats.current.days.map((day, index) => ({
            name: day.name,
            current: day.visits,
            previous: stats.previous.days[index].visits,
            currentNew: day.newVisitors,
            previousNew: stats.previous.days[index].newVisitors,
            currentRepeats: day.repeats,
            previousRepeats: stats.previous.days[index].repeats,
          }))
        : [],
    [stats]
  );

  const cumulativeData = useMemo(
    () =>
      stats
        ? stats.current.days.map((day, index) => ({
            name: day.name,
            current: day.cumulative,
            previous: stats.previous.days[index].cumulative,
          }))
        : [],
    [stats]
  );

  const roleData = useMemo(
    () =>
      stats
        ? ROLE_ORDER.map((role) => ({
            name: role,
            current: stats.current.roles[role],
            previous: stats.previous.roles[role],
          }))
        : [],
    [stats]
  );

  const hourlyData = useMemo(
    () =>
      stats
        ? Array.from({ length: 24 }, (_, hour) => ({
            name: `${String(hour).padStart(2, "0")}:00`,
            current: stats.current.days[hourlyDay - 1].hours[hour],
            previous: stats.previous.days[hourlyDay - 1].hours[hour],
          }))
        : [],
    [stats, hourlyDay]
  );

  const missingTime = stats
    ? EVENTS.map(
        (event) =>
          `${event.name}: ${number(
            stats[event.key].days[hourlyDay - 1].missingTime
          )}`
      ).join(" · ")
    : "";

  const scope = throughDay === 1 ? "Day 1" : `Days 1–${throughDay}`;

  const tooltipFormatter = (value, name) => [number(value), name];

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 3 },
        bgcolor: "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ maxWidth: 1500, mx: "auto" }}>
        <Box
          sx={{
            p: { xs: 2.5, md: 4 },
            mb: 3,
            borderRadius: 3,
            background: "linear-gradient(120deg, #0f172a, #1e3a8a)",
            color: "white",
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: "#bfdbfe",
              letterSpacing: 2,
            }}
          >
            EVENT ANALYTICS
          </Typography>

          <Typography variant="h4" fontWeight={800} mb={1}>
            Footfall comparison
          </Typography>

          <Typography sx={{ color: "#dbeafe" }}>
            PMTX 2026 vs PMTX 2024 · aligned by event day
          </Typography>

          <Stack direction="row" spacing={1} mt={2}>
            {EVENTS.map((event) => (
              <Chip
                key={event.key}
                label={event.name}
                sx={{
                  bgcolor: event.color,
                  color: event.key === "previous" ? "#172033" : "white",
                  fontWeight: 700,
                }}
              />
            ))}
          </Stack>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
          mb={2}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: 200,
              bgcolor: "white",
            }}
          >
            <InputLabel id="comparison-through-label">
              Comparison period
            </InputLabel>

            <Select
              labelId="comparison-through-label"
              label="Comparison period"
              value={throughDay}
              onChange={(event) => {
                const value = Number(event.target.value);

                setThroughDay(value);
                setHourlyDay((day) => Math.min(day, value));
              }}
            >
              {DAYS.map((day, index) => (
                <MenuItem key={day} value={index + 1}>
                  {comparisonLabel(index + 1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {scope} in both events. Percentage changes use 2024 as the baseline.
          </Typography>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            disabled={loading}
            onClick={loadData}
            sx={{ textTransform: "none" }}
          >
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          Choose the same completed event days for a fair comparison. An ongoing
          day is partial; zero means no recorded attendance, not proof that the
          day has finished.
        </Alert>

        {loading && (
          <Box mb={3}>
            <LinearProgress />
            <Typography mt={1} variant="body2">
              Loading both events…
            </Typography>
          </Box>
        )}

        {errors.map((message) => (
          <Alert severity="error" key={message} sx={{ mb: 2 }}>
            {message}
          </Alert>
        ))}

        {errors.length > 0 && (
          <Typography color="text.secondary" mb={2}>
            Both events must load before a comparison can be shown. Use Refresh
            to retry.
          </Typography>
        )}

        {stats && (
          <>
            <Grid container spacing={2} mb={3}>
              {[
                [
                  "Total footfall",
                  "visits",
                  "One attendance per person per day, including repeat days.",
                ],
                [
                  "Unique attendees",
                  "unique",
                  "Each person counted once within the selected days.",
                ],
                [
                  "Repeat-day visits",
                  "repeats",
                  "Additional attended days after a person's first attended day.",
                ],
              ].map(([title, metric, note]) => (
                <Grid item xs={12} md={4} key={metric}>
                  <MetricCard
                    title={title}
                    metric={metric}
                    note={note}
                    stats={stats}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Daily footfall"
                  subtitle="Side-by-side attendance for each event day."
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dailyData}
                      margin={{
                        top: 15,
                        right: 15,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={tooltipFormatter} />
                      <Legend />

                      {EVENTS.map((event) => (
                        <Bar
                          key={event.key}
                          dataKey={event.key}
                          name={event.name}
                          fill={event.color}
                          radius={[5, 5, 0, 0]}
                          maxBarSize={48}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Cumulative footfall"
                  subtitle="Running total of daily attendance, including repeat days."
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={cumulativeData}
                      margin={{
                        top: 15,
                        right: 15,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={tooltipFormatter} />
                      <Legend />

                      {EVENTS.map((event) => (
                        <Line
                          key={event.key}
                          type="linear"
                          dataKey={event.key}
                          name={event.name}
                          stroke={event.color}
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Attendees by category"
                  subtitle={`Unique attendees across ${scope.toLowerCase()}.`}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={roleData}
                      layout="vertical"
                      margin={{
                        top: 5,
                        right: 20,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />

                      <XAxis type="number" allowDecimals={false} />

                      <YAxis
                        type="category"
                        dataKey="name"
                        width={112}
                        tick={{ fontSize: 11 }}
                      />

                      <Tooltip formatter={tooltipFormatter} />
                      <Legend />

                      {EVENTS.map((event) => (
                        <Bar
                          key={event.key}
                          dataKey={event.key}
                          name={event.name}
                          fill={event.color}
                          radius={[0, 3, 3, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                      alignItems="center"
                    >
                      <Typography variant="h6" fontWeight={700}>
                        Hourly attendance
                      </Typography>

                      <FormControl size="small" sx={{ minWidth: 95 }}>
                        <InputLabel id="comparison-hour-label">Day</InputLabel>

                        <Select
                          labelId="comparison-hour-label"
                          label="Day"
                          value={hourlyDay}
                          onChange={(event) =>
                            setHourlyDay(Number(event.target.value))
                          }
                        >
                          {DAYS.slice(0, throughDay).map((day, index) => (
                            <MenuItem key={day} value={index + 1}>
                              Day {index + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Recorded daily scan time · IST · all 24 hours
                    </Typography>

                    <Box
                      sx={{
                        height: 270,
                        mt: 2,
                        minWidth: 0,
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={hourlyData}
                          margin={{
                            top: 10,
                            right: 15,
                            left: 0,
                            bottom: 0,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                          />

                          <XAxis
                            dataKey="name"
                            interval={3}
                            tick={{ fontSize: 11 }}
                          />

                          <YAxis allowDecimals={false} />
                          <Tooltip formatter={tooltipFormatter} />
                          <Legend />

                          {EVENTS.map((event) => (
                            <Line
                              key={event.key}
                              type="linear"
                              dataKey={event.key}
                              name={event.name}
                              stroke={event.color}
                              strokeWidth={2}
                              dot={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Missing/invalid scan time: {missingTime}. Included in
                      daily totals, omitted here.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <ComparisonTable stats={stats} throughDay={throughDay} />

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={2}
            >
              Exhibitor and Service roles are excluded. Unique attendees use
              _id, with user_id as fallback; people are counted separately in
              each event. Same-day re-entry scans are not available in this
              data.
            </Typography>

            {updatedAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.5}
              >
                Last refreshed:{" "}
                {updatedAt.toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                })}{" "}
                IST
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
